import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Account.module.css';
import { IconCheck, IconCopy, IconEye, IconEyeOff } from './AccountIcons';

/** `02ce16edf3…e3-1` — enough to recognise your own card, not enough to read
 *  over your shoulder. */
function maskCardId(value) {
  const id = String(value || '');
  if (id.length <= 14) return id;
  return `${id.slice(0, 10)}…${id.slice(-5)}`;
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Safari without clipboard permission, and any non-secure context.
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(el);
  if (!ok) throw new Error('copy-failed');
}

/**
 * The Card ID, which is the one piece of account data people are asked to read
 * out to support. It is masked by default, revealable, and copyable without
 * being revealed first — copying is the common case.
 */
export default function CardIdField({ cardId }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async () => {
    setError('');
    try {
      await copyText(cardId);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy on this device. Reveal the ID to copy it manually.');
    }
  }, [cardId]);

  return (
    <div className={styles.cardIdField}>
      <div className={styles.cardIdRow}>
        <code className={styles.cardIdValue} data-revealed={revealed ? 'true' : 'false'}>
          {revealed ? cardId : maskCardId(cardId)}
        </code>

        <div className={styles.cardIdActions}>
          <button
            type="button"
            className={styles.iconButtonSmall}
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide Card ID' : 'Reveal Card ID'}
            aria-pressed={revealed}
          >
            {revealed ? <IconEyeOff size={16} /> : <IconEye size={16} />}
          </button>
          <button
            type="button"
            className={styles.iconButtonSmall}
            onClick={handleCopy}
            aria-label="Copy Card ID"
          >
            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          </button>
        </div>
      </div>

      <p className={copied ? styles.cardIdConfirm : styles.cardIdHint} role="status">
        {error || (copied ? 'Card ID copied' : 'Quote this if you contact support about your card.')}
      </p>
    </div>
  );
}
